import {
  ChangeEvent,
  ChangeEventHandler,
  CSSProperties,
  FocusEventHandler,
  forwardRef,
  InputHTMLAttributes,
  KeyboardEventHandler,
  MouseEventHandler,
  MutableRefObject,
  ReactNode,
  Ref,
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
} from 'react';

import { FieldHeader, FieldMessage, joinIds } from '../Field/Field';
import styles from './Textfield.module.css';

type FieldElement = HTMLInputElement | HTMLTextAreaElement;

/** Attributes for the native `input` or `textarea`, for example `maxLength`. */
export type TextfieldInputAttributes = InputHTMLAttributes<FieldElement> & {
  [dataAttribute: `data-${string}`]: string | undefined;
};

/**
 * Adornments in the MUI `InputProps` shape. Kept so existing call sites work;
 * use `startAdornment`, `endAdornment` and `readOnly` in new code.
 */
export type TextfieldLegacyInputProps = {
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  readOnly?: boolean;
};

export interface TextfieldProps {
  /** Name of the input; also its id when no `id` is given. */
  name?: string;
  id?: string;
  label?: string;
  placeholder?: string;
  /** Text of the info button next to the label. */
  tooltip?: ReactNode;
  /** Element at the right end of the label row, for example a link. */
  actionItem?: ReactNode;
  /** A truthy value marks the field invalid and shows `helperText`. */
  error?: boolean | string | Error | null;
  /** Error message below the field, shown while `error` is set. */
  helperText?: ReactNode;
  /** Neutral help text below the field, replaced by the error message. */
  description?: ReactNode;
  /** Shows a red asterisk after the label. */
  requiredMark?: boolean;
  /** Native tooltip on the whole field, for values that do not fit. */
  textFieldTooltip?: string;
  value?: string | number | readonly string[];
  defaultValue?: string | number | readonly string[];
  type?: string;
  /** Renders a `textarea` instead of an `input`. */
  multiline?: boolean;
  /** Fixed number of visible lines of a multiline field. */
  rows?: number;
  /** A multiline field without `rows` grows with its text between these. */
  minRows?: number;
  maxRows?: number;
  /** Content before the input, for example a search icon. */
  startAdornment?: ReactNode;
  /** Content after the input, for example a copy button or a spinner. */
  endAdornment?: ReactNode;
  readOnly?: boolean;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  /** Height of a single-line field: 40 px (`medium`, default) or 32 px. */
  size?: 'small' | 'medium';
  className?: string;
  style?: CSSProperties;
  /** Attributes for the native element. */
  inputProps?: TextfieldInputAttributes;
  /** @deprecated Use `startAdornment`, `endAdornment` and `readOnly`. */
  InputProps?: TextfieldLegacyInputProps;
  /** @deprecated MUI prop without effect; the field always fills its container. */
  fullWidth?: boolean;
  /** @deprecated MUI prop without effect. */
  variant?: string;
  /** @deprecated MUI prop without effect. */
  color?: string;
  onChange?: ChangeEventHandler<FieldElement>;
  onBlur?: FocusEventHandler<FieldElement>;
  onFocus?: FocusEventHandler<FieldElement>;
  onKeyDown?: KeyboardEventHandler<FieldElement>;
  onKeyUp?: KeyboardEventHandler<FieldElement>;
  onClick?: MouseEventHandler<FieldElement>;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'data-testid'?: string;
}

const assignRef = <T,>(ref: Ref<T> | undefined, value: T | null) => {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    (ref as MutableRefObject<T | null>).current = value;
  }
};

/**
 * Text input of the Xelerate forms on Backstage UI: the BUI `FieldLabel`, an
 * info tooltip, a required mark, start and end adornments and a message line.
 *
 * It renders a native `input` (or `textarea` with `multiline`), so `ref`,
 * `onChange` and the other handlers get the DOM element and event, which keeps
 * it a drop-in for `react-hook-form` controllers.
 */
export const Textfield = forwardRef<HTMLInputElement, TextfieldProps>(
  (props, ref) => {
    const {
      name,
      id,
      label,
      placeholder = '',
      tooltip,
      actionItem,
      error = false,
      helperText,
      description,
      requiredMark = false,
      textFieldTooltip,
      value,
      defaultValue,
      type,
      multiline = false,
      rows,
      minRows,
      maxRows,
      startAdornment,
      endAdornment,
      readOnly,
      disabled,
      required,
      autoFocus,
      autoComplete = 'off',
      size = 'medium',
      className,
      style,
      inputProps,
      InputProps,
      onChange,
      onBlur,
      onFocus,
      onKeyDown,
      onKeyUp,
      onClick,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      'data-testid': testId,
    } = props;

    const generatedId = useId();
    const inputId = id ?? name ?? generatedId;
    const messageId = `${inputId}-message`;
    const isInvalid = !!error;
    const message = isInvalid && helperText ? helperText : description;

    const start = startAdornment ?? InputProps?.startAdornment;
    const end = endAdornment ?? InputProps?.endAdornment;
    const isReadOnly = readOnly ?? InputProps?.readOnly ?? inputProps?.readOnly;

    const elementRef = useRef<FieldElement | null>(null);
    const setElementRef = useCallback(
      (element: FieldElement | null) => {
        elementRef.current = element;
        assignRef(ref, element as HTMLInputElement | null);
      },
      [ref],
    );

    const autoGrow = multiline && rows === undefined;
    const resize = useCallback(() => {
      const element = elementRef.current;
      if (!autoGrow || !(element instanceof HTMLTextAreaElement)) {
        return;
      }
      const computed = window.getComputedStyle(element);
      const lineHeight = parseFloat(computed.lineHeight) || 20;
      const padding =
        parseFloat(computed.paddingTop) + parseFloat(computed.paddingBottom);
      element.style.height = 'auto';
      const min = (minRows ?? 1) * lineHeight + padding;
      const max = maxRows ? maxRows * lineHeight + padding : Infinity;
      const height = Math.min(Math.max(element.scrollHeight, min), max);
      element.style.height = `${height}px`;
      element.style.overflowY = element.scrollHeight > max ? 'auto' : 'hidden';
    }, [autoGrow, minRows, maxRows]);

    useLayoutEffect(resize, [resize, value]);

    const handleChange = (event: ChangeEvent<FieldElement>) => {
      onChange?.(event);
      resize();
    };

    const sharedProps = {
      ...inputProps,
      id: inputId,
      name,
      value,
      defaultValue,
      placeholder,
      readOnly: isReadOnly,
      disabled,
      required,
      autoFocus,
      autoComplete,
      className: [styles.input, 'xlrt-TextfieldInput', inputProps?.className]
        .filter(Boolean)
        .join(' '),
      'aria-label': ariaLabel ?? (label ? undefined : placeholder || name),
      'aria-invalid': isInvalid || undefined,
      'aria-describedby': joinIds(ariaDescribedBy, !!message && messageId),
      'data-testid': testId,
      onChange: handleChange,
      onBlur,
      onFocus,
      onKeyDown,
      onKeyUp,
      onClick,
    };

    return (
      <div
        className={[styles.root, 'xlrt-Textfield', className]
          .filter(Boolean)
          .join(' ')}
        style={style}
      >
        <FieldHeader
          label={label}
          htmlFor={inputId}
          tooltip={tooltip}
          requiredMark={requiredMark}
          actionItem={actionItem}
        />
        <div
          className={`${styles.inputWrapper} xlrt-TextfieldInputWrapper`}
          data-size={size}
          data-multiline={multiline || undefined}
          data-invalid={isInvalid || undefined}
          data-disabled={disabled || undefined}
          title={textFieldTooltip || undefined}
        >
          {start && (
            <span
              className={`${styles.adornment} xlrt-TextfieldAdornment`}
              data-position="start"
            >
              {start}
            </span>
          )}
          {multiline ? (
            <textarea
              {...(sharedProps as InputHTMLAttributes<HTMLTextAreaElement>)}
              ref={setElementRef}
              rows={rows ?? Math.max(1, Math.floor(minRows ?? 1))}
            />
          ) : (
            <input {...sharedProps} ref={setElementRef} type={type} />
          )}
          {end && (
            <span
              className={`${styles.adornment} xlrt-TextfieldAdornment`}
              data-position="end"
            >
              {end}
            </span>
          )}
        </div>
        <FieldMessage id={messageId} invalid={isInvalid && !!helperText}>
          {message}
        </FieldMessage>
      </div>
    );
  },
);

Textfield.displayName = 'Textfield';
